import { inject, Injectable } from '@angular/core';
import { ToastsService } from '@catch/services';
import { TranslocoService } from '@jsverse/transloco';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, exhaustMap, map, mergeMap, tap } from 'rxjs/operators';
import { MailTemplatesAdapter as EmailsAdapter } from '../../adapters/mail-templates.adapter';
import { confirmPauseWorkflows, workflowReferenceNames } from '../../functions/workflow-conflict';
import {
  createEmail,
  createEmailSuccess,
  deleteEmail,
  deleteEmailFailure,
  deleteEmailSuccess,
  loadAutoAcknowledge,
  loadAutoAcknowledgeFailure,
  loadAutoAcknowledgeSuccess,
  loadEmails,
  loadEmailsFailure,
  loadEmailsSuccess,
  updateAutoAcknowledge,
  updateAutoAcknowledgeFailure,
  updateAutoAcknowledgeSuccess,
  updateEmail,
  updateEmailFailure,
  updateEmailSuccess,
} from './emails.actions';

@Injectable()
export class EmailEffects {
  private readonly actions$ = inject(Actions);
  private readonly emailsAdapter = inject(EmailsAdapter);
  private readonly toastsService = inject(ToastsService);
  private readonly transloco = inject(TranslocoService);
  
  loadEmails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadEmails),
      exhaustMap(() =>
        this.emailsAdapter.getMailTemplates().pipe(
          map((emails) => loadEmailsSuccess({ emails })),
          catchError((error) => {
            return of(loadEmailsFailure({ error }));
          })
        )
      )
    )
  );

  loadAutoAcknowledge$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadAutoAcknowledge),
      exhaustMap(() =>
        this.emailsAdapter.getAcknowledge().pipe(
          map((autoAcknowledge) => loadAutoAcknowledgeSuccess({ autoAcknowledge })),
          catchError((error) => {
            return of(loadAutoAcknowledgeFailure({ error }));
          })
        )
      )
    )
  );

  createEmail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(createEmail),
      mergeMap((props) =>
        this.emailsAdapter.createMailTemplate(props.email).pipe(
          map((response) => createEmailSuccess({ email: { ...props.email, id: response.id } })),
          catchError((error) => {
            return of(deleteEmailFailure({ error }));
          })
        )
      )
    )
  );

  createEmailSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(createEmailSuccess),
        tap(() => {
          this.toastsService.success('company-tool.views.settings.company.emails.toasts.created');
        })
      ),
    { dispatch: false }
  );

  updateEmail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateEmail),
      mergeMap((props) =>
        this.emailsAdapter.editMailTemplate(props.email.id, props.email).pipe(
          map(() => updateEmailSuccess({ email: props.email })),
          catchError((error) => {
            return of(updateEmailFailure({ error }));
          })
        )
      )
    )
  );

  updateEmailSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(updateEmailSuccess),
        tap(() => {
          this.toastsService.success('company-tool.views.settings.company.emails.toasts.updated');
        })
      ),
    { dispatch: false }
  );

  updateAutoAcknowledgeEmail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateAutoAcknowledge),
      mergeMap((props) =>
        this.emailsAdapter.setAcknowledge(props.autoAcknowledge).pipe(
          map(() => updateAutoAcknowledgeSuccess({ autoAcknowledge: props.autoAcknowledge })),
          catchError((error) => {
            return of(updateAutoAcknowledgeFailure({ error }));
          })
        )
      )
    )
  );

  deleteEmail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(deleteEmail),
      mergeMap((props) =>
        this.emailsAdapter.deleteMailTemplate(props.email, props.force).pipe(
          map(() => deleteEmailSuccess({ id: props.email.id })),
          catchError((error) => {
            const names = workflowReferenceNames(error);
            if (names && !props.force && confirmPauseWorkflows(names, (name) => this.transloco.translate(name))) {
              return this.emailsAdapter.deleteMailTemplate(props.email, true).pipe(
                map(() => deleteEmailSuccess({ id: props.email.id })),
                catchError((retryError) => of(deleteEmailFailure({ error: retryError }))),
              );
            }
            return of(deleteEmailFailure({ error }));
          })
        )
      )
    )
  );

  deleteEmailSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(deleteEmailSuccess),
        tap(() => {
          this.toastsService.success('company-tool.views.settings.company.emails.toasts.deleted');
        })
      ),
    { dispatch: false }
  );

  loadEmailsFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(loadEmailsFailure),
        tap((action) => {
          // Todo: Add proper error handling. Maybe with a toasty?
          //alert(action.error.message);
        })
      ),
    { dispatch: false }
  );
}
